import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['cardholderName', 'cardNumber', 'cardExpiry', 'cardCvc', 'error', 'submit'];

  static values = {
    publishableKey: String,
    clientSecret: String,
    connectedAccountId: String,
  };

  async connect() {
    if (!this.hasCardNumberTarget || !this.publishableKeyValue || !this.clientSecretValue) return;

    await this.loadStripeJs();
    this.initializeStripe();
  }

  async loadStripeJs() {
    if (window.Stripe) return;

    await new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://js.stripe.com/v3/';
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Stripe.js'));
      document.head.appendChild(script);
    });
  }

  initializeStripe() {
    const options = {};

    if (this.connectedAccountIdValue) {
      options.stripeAccount = this.connectedAccountIdValue;
    }

    this.stripe = Stripe(this.publishableKeyValue, options);
    this.elements = this.stripe.elements();

    const style = {
      base: {
        fontFamily:
          'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        fontSize: '16px',
        color: '#212529',
        '::placeholder': {
          color: '#6c757d',
        },
      },
      invalid: {
        color: '#dc3545',
        iconColor: '#dc3545',
      },
    };

    this.cardNumberElement = this.elements.create('cardNumber', { style });
    this.cardExpiryElement = this.elements.create('cardExpiry', { style });
    this.cardCvcElement = this.elements.create('cardCvc', { style });

    this.cardNumberElement.mount(this.cardNumberTarget);
    this.cardExpiryElement.mount(this.cardExpiryTarget);
    this.cardCvcElement.mount(this.cardCvcTarget);
  }

  async submit(event) {
    event.preventDefault();

    if (!this.stripe || !this.cardNumberElement) {
      this.showError('Payment form is not ready. Please reload the page.');
      return;
    }

    const form = this.element;

    if (!form.checkValidity()) {
      form.classList.add('was-validated');
      return;
    }

    this.disableSubmit();

    const billingDetails = {};
    if (this.hasCardholderNameTarget && this.cardholderNameTarget.value) {
      billingDetails.name = this.cardholderNameTarget.value;
    }

    const result = await this.stripe.confirmCardPayment(this.clientSecretValue, {
      payment_method: {
        card: this.cardNumberElement,
        billing_details: billingDetails,
      },
    });

    if (result.error) {
      this.showError(result.error.message || 'Your payment could not be processed.');
      this.enableSubmit();
      return;
    }

    // At this point the PaymentIntent is confirmed. Let Rails continue the journey.
    form.requestSubmit();
  }

  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message;
    }
  }

  disableSubmit() {
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = true;
    }
  }

  enableSubmit() {
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = false;
    }
  }
}